import React, { useState } from "react";
import { useQuery } from 'react-query'
import { Box } from '@mui/material'

import { healthCheckApis } from "~/api";
import { HealthCheckResponse, STATUS_COLORS, checkNameMap } from "~/models";
import { useAppSelector } from '~/app/hooks'
import { isDarkTheme } from '~/redux/features'


// Tooltip rendering
const formatTooltip = (results: HealthCheckResponse["results"],isDark:boolean) => (
    <Box sx={{ padding: "10px" }}>
        {Object.entries(results).map(([key, val]) => {
            // Use dictionary mapping if available, otherwise show the original key as-is
            const displayKey = checkNameMap[key] || key;
            const color = isDark ? "#fff" : "#222";
            return (
                <Box key={key} sx={{ marginBottom: "8px" }}>
                    <Box component="strong" sx={{ color: color }}>{displayKey}:</Box>&nbsp;
                    <Box component="span" sx={{ color: STATUS_COLORS[val.status], fontWeight: "bold" }}>
                        {val.status}
                    </Box>
                    {val.description && (
                        <Box sx={{ fontSize: "12px", color: color, marginTop: "2px" }}>
                            {val.description}
                        </Box>
                    )}
                </Box>
            );
        })}
    </Box>
);
const lightTooltipStyle = {
    background: "#fff",
    border: "1px solid #ddd",
    boxShadow: "0 4px 18px rgba(0,0,0,0.15)",
};

const darkTooltipStyle = {
    background: "#23292f",
    border: "1px solid #444",
    boxShadow: "0 4px 18px rgba(0,0,0,0.9)",
};

export const HealthStatusIcon: React.FC = () => {
    const [hover, setHover] = useState(false);
    const isDark = useAppSelector(isDarkTheme);

    const { data: health, error } = useQuery<HealthCheckResponse, Error>(
        ["healthStatus"],
        () => healthCheckApis.getHealthCheck(), {
        onError(error) {
            console.error("Error fetching health data:", error);
        }
    })

    if (error || !health) {
        return (
            <Box sx={{ color: STATUS_COLORS.Unhealthy, fontWeight: "bold" }}>
            </Box>
        );
    }

    const color = STATUS_COLORS[health.status];
    const themeStyle = isDark ? darkTooltipStyle : lightTooltipStyle;
    return (
        <Box sx={{ position: "relative", display: "inline-block" }}>
            <Box
                sx={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    background: color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    cursor: "pointer",
                    transition: "background 0.2s",
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                aria-label={`Overall Status: ${health.status}`}
            >
                <Box component="span" sx={{ fontSize: "22px", color: "#fff" }}>
                </Box>
            </Box>

            {hover && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "45px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: "#222",
                        borderRadius: "5px",
                        zIndex: 999,
                        minWidth: "280px",
                        maxWidth: "400px",
                        fontSize: "14px",
                        ...themeStyle
                    }}
                >
                    {formatTooltip(health.results,isDark)}
                </Box>
            )}
        </Box>
    );
};
